import { useQuery } from "@tanstack/react-query";

export interface WidgetConfig {
  widgetType: string;
  sportLeague: string | null;
  metadata?: {
    matchInfo?: {
      homeTeam: string;
      awayTeam: string;
      league: string | null;
      matchDate: string | null;
    };
    xTrackerInfo?: {
      handle: string;
      startDate: string;
      endDate: string;
    };
  };
}

const FALLBACK: WidgetConfig = { widgetType: "none", sportLeague: null };

async function fetchWidgetConfig(slug: string): Promise<WidgetConfig> {
  const res = await fetch(`/api/event-widget/${slug}`);
  if (!res.ok) return FALLBACK;
  const data = await res.json();
  return {
    widgetType: data.widgetType || "none",
    sportLeague: data.sportLeague || null,
    metadata: data.metadata || {},
  };
}

/**
 * React Query hook for event widget configuration.
 * Independent of event data — fires immediately on mount (parallel with event fetch).
 * Long staleTime since widget config rarely changes.
 */
export function useEventWidgetConfig(slug: string | undefined) {
  return useQuery<WidgetConfig>({
    queryKey: ["event-widget", slug],
    queryFn: () => fetchWidgetConfig(slug!),
    enabled: !!slug,
    staleTime: 5 * 60_000,  // 5min — widget config changes rarely
    gcTime: 10 * 60_000,
    retry: false,
  });
}
